import React, { useState } from "react";
import moment from "moment";
import { CircularProgress } from "@mui/material";
import type { TeachrCourseSessionInfo } from "../../../Repositories/PunctualCourseRepository";
import { DV2 } from "../theme";
import { clock, dayLabel, monthName, studentFirstName } from "../format";
import { vocab } from "../vocabulary";
import { Card, useDv2Theme } from "../layout/Ui";

const PAGE = 5;

interface Props {
    child: any;
    month: moment.Moment;
    sessions: TeachrCourseSessionInfo[];
    loading?: boolean;
}

/** « Cours déjà prévus avec Ben », 5 par page (maquettes 1b et 1i). */
const UpcomingCoursesPanel = ({ child, month, sessions, loading }: Props) => {
    const { primary, fontFamily } = useDv2Theme();
    const [page, setPage] = useState(0);
    const sorted = [...sessions].sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
    const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE));
    const current = Math.min(page, pageCount - 1);
    const shown = sorted.slice(current * PAGE, current * PAGE + PAGE);
    const nav = (label: string, enabled: boolean, onClick: () => void, glyph: string) => (
        <button type="button" aria-label={label} disabled={!enabled} onClick={onClick} style={{ background: "none", border: "none", color: enabled ? primary : DV2.border, fontSize: 18, cursor: enabled ? "pointer" : "default", padding: "0 6px", fontFamily }}>{glyph}</button>
    );

    return (
        <Card style={{ display: "flex", flexDirection: "column", gap: 10, fontFamily }} aria-label={`${vocab.courses()} déjà prévus avec ${studentFirstName(child)}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{vocab.courses().charAt(0).toUpperCase() + vocab.courses().slice(1)} déjà prévus avec {studentFirstName(child)}</div>
                <span style={{ fontSize: 12, color: DV2.muted, whiteSpace: "nowrap" }}>{loading ? <CircularProgress size={12} /> : `${sorted.length} en ${monthName(month)}`}</span>
            </div>
            {!loading && sorted.length === 0 && (
                <div style={{ fontSize: 13, color: DV2.muted }}>Aucun {vocab.course()} prévu avec {studentFirstName(child)} en {monthName(month)}.</div>
            )}
            {shown.map((session, i) => (
                <div key={`${session.courseId ?? "c"}-${session.date}-${i}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, gap: 8 }}>
                    <span style={{ color: DV2.muted }}>{dayLabel(session.date)}</span>
                    <b>{clock(session.date)} à {clock(session.endDate)}</b>
                </div>
            ))}
            {sorted.length > PAGE && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: DV2.muted }}>
                    <span>{current * PAGE + 1} à {Math.min(sorted.length, (current + 1) * PAGE)} sur {sorted.length}</span>
                    <div>
                        {nav("Précédents", current > 0, () => setPage(current - 1), "‹")}
                        {nav("Suivants", current < pageCount - 1, () => setPage(current + 1), "›")}
                    </div>
                </div>
            )}
        </Card>
    );
};

export default UpcomingCoursesPanel;
